import React, { memo, useCallback, useMemo, useState } from 'react';

import { Modal, Table } from '@components/common';

import { useHistoryDetails } from '@hooks/historyCheck/useHistoryDetails';
import { useHistoryDetailsAwtgRan } from '@hooks/historyCheck/useHistoryDetailsAwtgRan';
import { useMutateExportHcAwtgRanPath } from '@hooks/historyCheck/useMutateExportHcAwtgRanPath';
import { useMutateExportHcBranch } from '@hooks/historyCheck/useMutateExportHcBranch';
import { useQueryHistoryCheckAwtgRanMoIds } from '@hooks/historyCheck/useQueryHistoryCheckAwtgRanMoIds';

import { generateFilters, getDynamicColumns } from '@utils/cmModule';

import { IManagedObjects } from '@interfaces/cmModule/cmModule';
import { IBranchTable } from '@interfaces/cmModule/configurationComparison';
import { IHistoryCheckDetail } from '@interfaces/cmModule/historyCheck';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IMultiDataTableFilter,
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TMultiDataFilterItem,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { TableFilterTypes } from '@constants/table';
import {
	BRANCH_TABLE_SIZE,
	filterWidth,
	PARAMETER_TABLE_SIZE,
	searchTooltipText,
} from './constants';

import HistoryCheckParameter from './HistoryCheckParameter';
import { createDynamicMoColumns, getActionColumn, getColumns, getParametersToShow } from './utils';

import styles from './HistoryCheckBranch.module.scss';

const convertRanValuesToTimestamp = (values: any[]) => {
	if (!values || !Array.isArray(values)) {
		return [];
	}

	return values.map(v => {
		if (v.date && v.time) {
			const dateTimeStr = `${v.date} ${v.time}`;
			const timestamp = Date.parse(dateTimeStr);

			return {
				date: isNaN(timestamp) ? Date.now() : timestamp,
				value: v.value,
			};
		}

		return v;
	});
};

interface IHistoryCheckBranchProps {
	selectedTableBranch: IBranchTable;
	sameDay?: number;
	vendor?: string;
	isMavenirVendor: boolean;
	isAwtgRanVendor?: boolean;
}

const HistoryCheckBranch = ({
	selectedTableBranch,
	sameDay,
	isMavenirVendor,
	isAwtgRanVendor,
	vendor,
}: IHistoryCheckBranchProps) => {
	const [selectedSiteId, setSelectedSiteId] = useState<TSelectOption<string> | null>(null);
	const [selectedParamName, setSelectedParamName] = useState<TSelectOption<string> | null>(null);
	const [selectedConfigId, setSelectedConfigId] = useState<TSelectOption<string> | null>(null);
	const [selectedGnbId, setSelectedGnbId] = useState<TSelectOption<string> | null>(null);
	const [selectedGeneratedFilters, setSelectedGeneratedFilters] = useState<Record<string, TSelectOption>>({});
	const [isOpenHistoryCheckParameter, setIsOpenHistoryCheckParameter] = useState(false);
	const [selectedParameter, setSelectedParameter] = useState<IHistoryCheckDetail>();
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [isOnlyChanged, setIsOnlyChanged] = useState(false);
	const [multiDataFilter, setMultiDataFilter] = useState<TMultiDataFilterItem[]>([{ moName: null, moId: null }]);
	const [selectedNeName, setSelectedNeName] = useState<TSelectOption<string>[] | null>(null);

	const comparisonId = selectedTableBranch?.id;
	const dumpHistoryId = selectedTableBranch?.dumpHistoryId;
	const dataModelPathId = selectedTableBranch?.dataModelPathId;

	const managedObjects = useMemo(() => {
		if (isAwtgRanVendor) {
			return multiDataFilter
				.filter(option => option.moName?.value && option.moId?.value)
				.map(option => ({
					moName: option.moName?.value || '',
					moId: option.moId?.value || '',
				}))
				.filter(mo => mo.moName && mo.moId);
		}

		return Object.entries(selectedGeneratedFilters).reduce<IManagedObjects[]>((acc, [key, option]) => {
			const moItem = option?.value ? { moName: key, moId: option.value.toString() } : null;

			return moItem
				? [...acc, moItem]
				: acc;
		}, []);
	}, [selectedGeneratedFilters, multiDataFilter, isAwtgRanVendor]);

	const defaultHistoryData = useHistoryDetails({
		comparisonId,
		page,
		pageSize,
		paramName: selectedParamName?.value,
		siteId: selectedSiteId?.value,
		gnbId: selectedGnbId?.value,
		configId: selectedConfigId?.value,
		searchValue: searchValue,
		sortDirection,
		sortFieldName,
		isMavenirVendor,
		managedObjects,
		onlyChanged: isOnlyChanged,
		isAwtgRanVendor,
	});

	const awtgRanHistoryData = useHistoryDetailsAwtgRan({
		dumpHistoryId: dumpHistoryId || '',
		dataModelPathId: dataModelPathId || '',
		managedObjects,
		networkElementName: selectedNeName?.length ? selectedNeName[0]?.value || '' : '',
		page,
		pageSize,
		paramSearchValue: searchValue,
		sortDirection,
		sortFieldName,
		isAwtgRanVendor,
	});

	const {
		historyDetails,
		historyDetailsTotal,
		configIdsOptions,
		gnbIdsOptions,
		detailsMoFilters,
		moNames,
		isLoadingDetails,
		parameterFilterOptions,
		siteIdFilterOptions,
		moIdsOptions,
		moNamesOptions,
		neNamesOptions,
		dumpHistoryId: awtgRanDumpHistoryId,
		dataModelPathId: awtgRanDataModelPathId,
	} = isAwtgRanVendor ? {
		...awtgRanHistoryData,
		configIdsOptions: [],
		gnbIdsOptions: [],
		detailsMoFilters: [],
		moNames: [],
		parameterFilterOptions: [],
		siteIdFilterOptions: [],
	} : {
		...defaultHistoryData,
		moIdsOptions: [],
		moNamesOptions: [],
		neNamesOptions: [],
		dumpHistoryId: null,
		dataModelPathId: null,
	};

	const { exportBranch } = useMutateExportHcBranch();
	const { exportPath } = useMutateExportHcAwtgRanPath();

	const openHistoryCheckParameter = useCallback(() => setIsOpenHistoryCheckParameter(true), []);
	const closeHistoryCheckParameter = useCallback(() => {
		setIsOpenHistoryCheckParameter(false);
		setIsOnlyChanged(false);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort: handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: '',
			name: 'name',
			handler: (searchValue: string) => setSearchValue(searchValue),
			tooltipText: searchTooltipText,
		},
	], [setSearchValue]);

	const moFilters = useMemo(() => {
		return generateFilters(
			detailsMoFilters,
			selectedGeneratedFilters,
			setSelectedGeneratedFilters,
		);
	}, [detailsMoFilters, selectedGeneratedFilters]);

	const [selectedMoNameForFetch, setSelectedMoNameForFetch] = useState<string>('');

	const effectiveDumpHistoryId = isAwtgRanVendor ? awtgRanDumpHistoryId : dumpHistoryId;
	const effectiveDataModelPathId = isAwtgRanVendor ? awtgRanDataModelPathId : dataModelPathId;

	const { moIdsOptions: dynamicMoIdsOptions } = useQueryHistoryCheckAwtgRanMoIds({
		dumpHistoryId: effectiveDumpHistoryId || '',
		dataModelPathId: effectiveDataModelPathId || '',
		moName: selectedMoNameForFetch,
		isAwtgRanVendor,
	});

	const getSubOptions = useCallback((moName: string) => {
		if (!effectiveDumpHistoryId || !effectiveDataModelPathId || !moName) {
			return;
		}

		setSelectedMoNameForFetch(moName);
	}, [effectiveDumpHistoryId, effectiveDataModelPathId]);

	const multiDataFilters: IMultiDataTableFilter | undefined = useMemo(() => {
		if (!isAwtgRanVendor) {
			return undefined;
		}

		const availableMoIds = selectedMoNameForFetch ? dynamicMoIdsOptions : moIdsOptions;

		return {
			selectedFilters: multiDataFilter,
			setSelectedFilters: setMultiDataFilter,
			clearSelectedFilters: () => setMultiDataFilter([{ moName: null, moId: null }]),
			filterProps: {
				maxMenuHeight: 200,
				maxWidth: '200px',
				minWidth: '200px',
				placeholder: 'Object',
				subPlaceholder: 'ID',
				name: 'moName',
				subName: 'moId',
				options: moNamesOptions.filter(
					(o) => !multiDataFilter.find((f) => f.moName?.value === o?.value),
				),
				subOptions: availableMoIds,
				width: '100%',
				getSubOptions,
			},
		};
	}, [
		isAwtgRanVendor,
		multiDataFilter,
		moNamesOptions,
		moIdsOptions,
		dynamicMoIdsOptions,
		selectedMoNameForFetch,
		getSubOptions,
	]);

	const filters: TTableFilter[] = useMemo(() => {
		if (isAwtgRanVendor) {
			return [
				{
					type: TableFilterTypes.MULTISELECT,
					name: 'neName',
					placeholder: 'NE ID',
					option: selectedNeName,
					options: neNamesOptions,
					minWidth: filterWidth,
					setState: setSelectedNeName,
					defaultValue: null,
				},
			];
		}

		return isMavenirVendor
			? [
				{
					name: 'gnbId',
					placeholder: 'NODE ID',
					option: selectedGnbId,
					options: gnbIdsOptions,
					minWidth: filterWidth,
					setState: setSelectedGnbId,
					defaultValue: 'gnbId',
				},
				{
					name: 'configId',
					placeholder: 'CNF ID',
					option: selectedConfigId,
					options: configIdsOptions,
					minWidth: filterWidth,
					setState: setSelectedConfigId,
					defaultValue: 'configId',
				},
				...moFilters,
			] : [
				{
					name: 'paramName',
					placeholder: 'Parameter',
					option: selectedParamName,
					options: parameterFilterOptions,
					minWidth: '200px',
					setState: setSelectedParamName,
					defaultValue: 'paramName',
				},
				{
					name: 'siteId',
					placeholder: 'Site ID',
					option: selectedSiteId,
					options: siteIdFilterOptions,
					minWidth: filterWidth,
					setState: setSelectedSiteId,
					defaultValue: 'siteId',
				},
			];
	}, [
		isAwtgRanVendor,
		isMavenirVendor,
		moFilters,
		configIdsOptions,
		gnbIdsOptions,
		selectedGnbId,
		selectedConfigId,
		selectedSiteId,
		siteIdFilterOptions,
		parameterFilterOptions,
		selectedParamName,
		selectedNeName,
		neNamesOptions,
	]);

	const baseColumns = useMemo(() => getColumns(isMavenirVendor, isAwtgRanVendor), [isMavenirVendor, isAwtgRanVendor]);

	const dynamicMoColumns = useMemo(() => {
		return isAwtgRanVendor ? createDynamicMoColumns(historyDetails) : [];
	}, [isAwtgRanVendor, historyDetails]);

	const columns = useMemo(() => {
		return isAwtgRanVendor
			? [...dynamicMoColumns, ...baseColumns]
			: baseColumns;
	}, [baseColumns, dynamicMoColumns, isAwtgRanVendor]);

	const actionColumn = useMemo(() => getActionColumn({
		openTable: openHistoryCheckParameter,
		setSelectedParameter,
	}), [
		openHistoryCheckParameter,
		setSelectedParameter,
	]);

	const dynamicColumns = useMemo(() => getDynamicColumns(moNames), [moNames]);

	const vendorColumns = useMemo(() => {
		if (isAwtgRanVendor) {
			return columns;
		}
		return isMavenirVendor ? [...columns, ...dynamicColumns] : columns;
	}, [columns, dynamicColumns, isMavenirVendor, isAwtgRanVendor]);

	const branchColumns = useMemo(() => {
		return [...vendorColumns, actionColumn];
	}, [
		vendorColumns,
		actionColumn,
	]);

	const stickyColumnIndexes = useMemo(() => {
		if (isAwtgRanVendor) {
			const dynamicIndexes = dynamicMoColumns.map((_, index) => index);
			const parameterIndex = dynamicMoColumns.length;
			const neNameIndex = dynamicMoColumns.length + 1;

			return [...dynamicIndexes, parameterIndex, neNameIndex];
		}

		return [0, 1, 2];
	}, [isAwtgRanVendor, dynamicMoColumns]);

	const parametersToShow = getParametersToShow({ vendorColumns: vendorColumns.slice(2), selectedParameter });

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPage(defaultMeta.page);
		setPageSize(pageSize);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: historyDetailsTotal,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [historyDetailsTotal, page, pageSize, pageHandler, pageSizeHandler]);

	const handleExport = useCallback(() => {
		if (isAwtgRanVendor) {
			if (!dumpHistoryId || !dataModelPathId) return;

			const filter: {
				managedObjects?: IManagedObjects[];
				networkElementName?: string;
				paramSearchValue?: string;
			} = {};

			if (managedObjects.length) {
				filter.managedObjects = managedObjects;
			}

			if (selectedNeName?.length && selectedNeName[0]?.value) {
				filter.networkElementName = selectedNeName[0].value;
			}

			if (searchValue) {
				filter.paramSearchValue = searchValue;
			}

			const dto = {
				dataModelPathId,
				dumpHistoryId,
				filter,
				isAll: true,
			};

			exportPath(dto);
		} else {
			if (!comparisonId) return;

			const dto = {
				comparisonId,
				ids: [],
				isAll: true,
				filter: null,
				vendor: vendor,
			};

			exportBranch(dto);
		}
	}, [
		isAwtgRanVendor,
		dumpHistoryId,
		dataModelPathId,
		managedObjects,
		selectedNeName,
		searchValue,
		exportPath,
		comparisonId,
		vendor,
		exportBranch,
	]);

	return (
		<div className={styles.branchTableWrapper}>
			{selectedParameter && (
				<Modal
					height={PARAMETER_TABLE_SIZE}
					isOpen={!!(isOpenHistoryCheckParameter && selectedParameter)}
					onClose={closeHistoryCheckParameter}
					title={isAwtgRanVendor
						? `${selectedTableBranch?.dataModelPath}/${selectedParameter.paramName}`
						: `${selectedTableBranch?.distName}/${selectedParameter.paramName}`}
					width={PARAMETER_TABLE_SIZE}
				>
					<HistoryCheckParameter
						dataModelPathId={dataModelPathId}
						dumpHistoryId={dumpHistoryId}
						isAwtgRanVendor={isAwtgRanVendor}
						isOnlyChanged={isOnlyChanged}
						parametersId={selectedParameter.id}
						parametersToShow={parametersToShow}
						sameDay={sameDay}
						setIsOnlyChanged={setIsOnlyChanged}
						values={isAwtgRanVendor
							? convertRanValuesToTimestamp(selectedParameter.values)
							: selectedParameter.values}
						vendor={vendor}
					/>
				</Modal>
			)}
			<Table
				columns={branchColumns}
				data={historyDetails}
				exportTooltipText='Export Branch to Excel'
				filters={filters}
				handleCustomExport={handleExport}
				isExport
				isLoading={isLoadingDetails}
				isPinColumns={isAwtgRanVendor}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				maxHeight={BRANCH_TABLE_SIZE}
				multiDataFilters={multiDataFilters}
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
				stickyLeftColumnIndexes={stickyColumnIndexes}
			/>
		</div>
	);
};

export default memo(HistoryCheckBranch);
